import React, { useState, useEffect } from 'react';
import { Send, FileText, Loader, CheckCircle, XCircle, Clock, Plus, Trash2, MessageSquare, UserCheck, Sparkles, Bot, Users, ChevronDown, ChevronUp, Eye, Link, Copy, FlaskConical } from 'lucide-react';
import {
  getCandidates,
  getRequirements,
  getOrganisations,
  getSentInterviews,
  sendInterview,
  generateQuestions,
  generateToken,
  checkQuestions,
  getGeneratedQuestions,
  getProctors,
  getInterviewers,
  assignProctor
} from '../services/api';




function SendInterview() {
  // State
  const [candidates, setCandidates] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [proctors, setProctors] = useState([]);
  const [selectedProctor, setSelectedProctor] = useState('');
  const [interviewType, setInterviewType] = useState('ai'); // 'ai' or 'panel'
  const [interviewers, setInterviewers] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState([]); // interviewer user_ids
  const [sentInterviews, setSentInterviews] = useState([]);
  const [activeTab, setActiveTab] = useState('send');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [questionsGenerated, setQuestionsGenerated] = useState(false);
  const [checkingQuestions, setCheckingQuestions] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [error, setError] = useState('');
  const [enabledPhases, setEnabledPhases] = useState({
    realTimeQA: true,
    imageQuestions: true,
    systemDesign: true,
    codingChallenge: true
  });
  const [totalQuestions, setTotalQuestions] = useState(9);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [difficultyLevel, setDifficultyLevel] = useState(5);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [testToken, setTestToken] = useState(null);   // { token, interview_url, expires_at }
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copiedField, setCopiedField] = useState(null); // 'token' | 'url'
  const [sentInvitationResult, setSentInvitationResult] = useState(null); // { token, interview_url, email }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [candidatesData, requirementsData, organisationsData, historyData, proctorsData, interviewersData] = await Promise.all([
        getCandidates(),
        getRequirements(),
        getOrganisations(),
        getSentInterviews(),
        getProctors().catch(() => []),
        getInterviewers().catch(() => [])
      ]);

      setCandidates(candidatesData);
      setRequirements(requirementsData);
      setOrganisations(organisationsData);
      setSentInterviews(historyData);
      setProctors(proctorsData || []);
      setInterviewers(interviewersData || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data. Please refresh the page.');
    }
  };

  const fetchQuestionPreview = async (candidateId, requirementId, organisationId) => {
    setLoadingPreview(true);
    try {
      const data = await getGeneratedQuestions(candidateId, requirementId, organisationId);
      setPreviewQuestions(data.questions || []);
      setPreviewOpen(true);
    } catch {
      setPreviewQuestions([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const checkQuestionsStatus = async (candidateId, requirementId, organisationId) => {
    if (!candidateId || !requirementId || !organisationId) return;

    setCheckingQuestions(true);
    try {
      const result = await checkQuestions(candidateId, requirementId, organisationId);
      setQuestionsGenerated(result.questions_exist);
      if (result.questions_exist) {
        fetchQuestionPreview(candidateId, requirementId, organisationId);
      }
    } catch (error) {
      console.error('Failed to check questions:', error);
      setQuestionsGenerated(false);
    } finally {
      setCheckingQuestions(false);
    }
  };

  const handleCandidateSelect = (candidateId) => {
    setSelectedCandidate(candidateId);
    setQuestionsGenerated(false);
    setSentInvitationResult(null);
    if (candidateId && selectedRequirement && selectedOrganisation) {
      checkQuestionsStatus(candidateId, selectedRequirement, selectedOrganisation);
    }
  };

  const handleRequirementSelect = (requirementId) => {
    setSelectedRequirement(requirementId);
    setQuestionsGenerated(false);
    setSentInvitationResult(null);
    if (selectedCandidate && requirementId && selectedOrganisation) {
      checkQuestionsStatus(selectedCandidate, requirementId, selectedOrganisation);
    }
  };

  const handleOrganisationSelect = (organisationId) => {
    setSelectedOrganisation(organisationId);
    setQuestionsGenerated(false);
    setSentInvitationResult(null);
    if (selectedCandidate && selectedRequirement && organisationId) {
      checkQuestionsStatus(selectedCandidate, selectedRequirement, organisationId);
    }
  };

  const addCustomQuestion = () => {
    if (customQuestions.length >= totalQuestions) {
      alert(`Cannot add more than ${totalQuestions} custom questions (matches total Q&A count)`);
      return;
    }
    setCustomQuestions([...customQuestions, { question: '', expectedKeywords: '' }]);
  };

  const removeCustomQuestion = (index) => {
    setCustomQuestions(customQuestions.filter((_, i) => i !== index));
  };

  const updateCustomQuestion = (index, field, value) => {
    const updated = [...customQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setCustomQuestions(updated);
  };

  const handleGenerateQuestions = async () => {
    if (!selectedCandidate || !selectedRequirement || !selectedOrganisation) {
      alert('Please select candidate, requirement, and organization');
      return;
    }

    // Validate custom questions have text
    const validCustom = customQuestions.filter(q => q.question.trim());
    if (customQuestions.length > 0 && validCustom.length !== customQuestions.length) {
      alert('Please fill in all custom question fields or remove empty ones');
      return;
    }

    setGenerating(true);
    setGenerateError('');

    try {
      await generateQuestions(
        selectedCandidate, selectedRequirement, selectedOrganisation,
        enabledPhases, totalQuestions, validCustom, difficultyLevel
      );
      setQuestionsGenerated(true);
      await fetchQuestionPreview(selectedCandidate, selectedRequirement, selectedOrganisation);
    } catch (error) {
      console.error('Failed to generate questions:', error);
      setGenerateError(error.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendInterview = async () => {
    if (!selectedCandidate || !selectedRequirement || !selectedOrganisation) {
      alert('Please select candidate, requirement, and organization');
      return;
    }

    if (!questionsGenerated) {
      alert('Please generate questions first');
      return;
    }

    setSending(true);

    try {
      const result = await sendInterview(selectedCandidate, selectedRequirement, selectedOrganisation, {
        assignedProctorId: interviewType === 'ai' ? (selectedProctor || null) : null,
        interviewType,
        panelInterviewerIds: interviewType === 'panel' ? selectedPanel : [],
      });

      setSentInvitationResult({
        token: result.token,
        interview_url: result.interview_url,
        email: result.email,
      });

      // Reset form
      setSelectedCandidate('');
      setSelectedRequirement('');
      setSelectedOrganisation('');
      setSelectedProctor('');
      setInterviewType('ai');
      setSelectedPanel([]);
      setQuestionsGenerated(false);
      setPreviewQuestions([]);
      setTestToken(null);
      setEnabledPhases({ realTimeQA: true, imageQuestions: true, systemDesign: true, codingChallenge: true });
      setTotalQuestions(9);
      setCustomQuestions([]);
      setDifficultyLevel(5);
      
      // Refresh history
      const historyData = await getSentInterviews();
      setSentInterviews(historyData);
    } catch (error) {
      console.error('Failed to send interview:', error);
      alert(error.response?.data?.detail || 'Failed to send interview');
    } finally {
      setSending(false);
    }
  };

  const handleGetTestToken = async () => {
    if (!selectedCandidate || !selectedRequirement || !selectedOrganisation || !questionsGenerated) return;
    setGeneratingToken(true);
    setTestToken(null);
    try {
      const data = await generateToken(selectedCandidate, selectedRequirement, selectedOrganisation, {
        assignedProctorId: interviewType === 'ai' ? (selectedProctor || null) : null,
        interviewType,
        panelInterviewerIds: interviewType === 'panel' ? selectedPanel : [],
      });
      setTestToken(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate test token');
    } finally {
      setGeneratingToken(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // Reassign (or clear) the proctor on an already-sent interview.
  const handleReassignProctor = async (token, assignedProctorId) => {
    try {
      await assignProctor({ token, assignedProctorId: assignedProctorId || null });
      const historyData = await getSentInterviews();
      setSentInterviews(historyData);
    } catch (error) {
      console.error('Failed to reassign proctor:', error);
      alert(error.response?.data?.detail || 'Failed to reassign proctor');
    }
  };

  // Proctors / interviewers selectable for the chosen organization.
  const proctorsForOrg = proctors.filter(
    (p) => !selectedOrganisation || p.organization_id === selectedOrganisation
  );
  const interviewersForOrg = interviewers.filter(
    (p) => !selectedOrganisation || p.organization_id === selectedOrganisation
  );
  const togglePanelMember = (userId) => {
    setSelectedPanel((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getStatusBadge = (interview) => {
    if (interview.used) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </span>
      );
    }
    
    const expiresAt = new Date(interview.expires_at);
    const now = new Date();
    
    if (expiresAt < now) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-500/15 text-brand-300">
        <Clock className="w-3 h-3 mr-1" />
        Pending
      </span>
    );
  };

  return (
    <div className="send-interview-container">
      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-ink-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('send')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'send'
                  ? 'border-brand-500 text-brand-400'
                  : 'border-transparent text-ink-400 hover:text-ink-300 hover:border-ink-700'
              }`}
            >
              <Send className="w-4 h-4 inline-block mr-2" />
              Send Interview
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-brand-500 text-brand-400'
                  : 'border-transparent text-ink-400 hover:text-ink-300 hover:border-ink-700'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-2" />
              Sent Interviews
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'send' ? (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-ink-100 mb-4">
              Send New Interview Invitation
            </h2>
            <p className="text-sm text-ink-300 mb-6">
              Select a candidate and job requirement to send an interview invitation email.
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Candidate Selection */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">
              Select Candidate
            </label>
            <select
              value={selectedCandidate}
              onChange={(e) => handleCandidateSelect(e.target.value)}
              className="w-full px-4 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="">Choose a candidate...</option>
              {candidates.map((candidate) => (
                <option key={candidate.candidate_id} value={candidate.candidate_id}>
                  {candidate.name} ({candidate.email})
                </option>
              ))}
            </select>
          </div>

          {/* Requirement Selection */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">
              Select Job Requirement
            </label>
            <select
              value={selectedRequirement}
              onChange={(e) => handleRequirementSelect(e.target.value)}
              className="w-full px-4 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="">Choose a requirement...</option>
              {requirements.map((req) => (
                <option key={req.requirement_id} value={req.requirement_id}>
                  {req.position} - {req.experience_level}
                </option>
              ))}
            </select>
          </div>

          {/* Organization Selection */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">
              Select Organization
            </label>
            <select
              value={selectedOrganisation}
              onChange={(e) => handleOrganisationSelect(e.target.value)}
              className="w-full px-4 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="">Choose an organization...</option>
              {organisations.filter(org => org.organisation_id).map((org) => (
                <option key={org.organisation_id} value={org.organisation_id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          {/* Interview Type: AI-led (with optional proctor) vs human panel (copilot) */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">Interview Type</label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setInterviewType('ai')}
                className={`text-left p-4 rounded-lg border transition-colors ${
                  interviewType === 'ai'
                    ? 'border-brand-500 bg-brand-500/10'
                    : 'border-ink-700 bg-ink-800 hover:border-ink-600'
                }`}
              >
                <div className="flex items-center gap-2 font-medium text-ink-100">
                  <Bot className="w-4 h-4 text-brand-400" /> AI Interview
                </div>
                <p className="text-xs text-ink-400 mt-1">
                  AIETA conducts the interview. Optionally assign a proctor to monitor and take over.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setInterviewType('panel')}
                className={`text-left p-4 rounded-lg border transition-colors ${
                  interviewType === 'panel'
                    ? 'border-brand-500 bg-brand-500/10'
                    : 'border-ink-700 bg-ink-800 hover:border-ink-600'
                }`}
              >
                <div className="flex items-center gap-2 font-medium text-ink-100">
                  <Sparkles className="w-4 h-4 text-brand-400" /> Panel Interview (Copilot)
                </div>
                <p className="text-xs text-ink-400 mt-1">
                  Your panel conducts the interview; the AI copilot gives live evaluation and follow-ups.
                </p>
              </button>
            </div>
          </div>

          {/* Proctor (AI interview) */}
          {interviewType === 'ai' && (
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-2">
                <UserCheck className="w-4 h-4 inline-block mr-1" />
                Assign Proctor <span className="text-ink-500 font-normal">(optional)</span>
              </label>
              <select
                value={selectedProctor}
                onChange={(e) => setSelectedProctor(e.target.value)}
                disabled={!selectedOrganisation}
                className="w-full px-4 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">No proctor (AI-only interview)</option>
                {proctorsForOrg.map((p) => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.full_name} ({p.email})
                  </option>
                ))}
              </select>
              <p className="text-xs text-ink-400 mt-1">
                {!selectedOrganisation
                  ? 'Select an organization to choose a proctor.'
                  : proctorsForOrg.length === 0
                    ? 'No proctors in this organization yet — an admin can create proctor accounts.'
                    : 'The assigned proctor sees this interview on their personal dashboard and can join live.'}
              </p>
            </div>
          )}

          {/* Panel (Copilot interview) */}
          {interviewType === 'panel' && (
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-2">
                <Users className="w-4 h-4 inline-block mr-1" />
                Interview Panel <span className="text-ink-500 font-normal">(select one or more)</span>
              </label>
              {!selectedOrganisation ? (
                <p className="text-xs text-ink-400">Select an organization to choose panelists.</p>
              ) : interviewersForOrg.length === 0 ? (
                <p className="text-xs text-ink-400">
                  No interviewers in this organization yet — an admin can create interviewer accounts.
                </p>
              ) : (
                <div className="space-y-2 bg-ink-800 border border-ink-700 rounded-lg p-3 max-h-56 overflow-y-auto">
                  {interviewersForOrg.map((p) => (
                    <label key={p.user_id} className="flex items-center gap-3 text-sm text-ink-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPanel.includes(p.user_id)}
                        onChange={() => togglePanelMember(p.user_id)}
                        className="rounded"
                      />
                      <span>{p.full_name} <span className="text-ink-500">({p.email})</span></span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-ink-400 mt-1">
                {selectedPanel.length > 0
                  ? `${selectedPanel.length} panelist(s) selected — each sees this interview on their Interview Copilot dashboard.`
                  : 'Panelists conduct the interview with live AI assistance.'}
              </p>
            </div>
          )}

          {/* Interview Phases Checklist */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">
              Interview Phases
            </label>
            <p className="text-xs text-ink-400 mb-3">
              Choose which phases to include. Uncheck phases not relevant for this role.
            </p>
            <div className="space-y-2 bg-ink-800 border border-ink-700 rounded-lg p-4">
              <label className="flex items-center gap-3 text-sm text-ink-400">
                <input type="checkbox" checked disabled className="rounded" />
                <span><strong>Phase 1:</strong> Real-time Q&A</span>
                <span className="text-xs text-ink-500 ml-auto">(always included)</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-ink-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabledPhases.imageQuestions}
                  onChange={(e) => setEnabledPhases(prev => ({ ...prev, imageQuestions: e.target.checked }))}
                  className="rounded"
                  disabled={questionsGenerated}
                />
                <span><strong>Phase 2:</strong> Image Questions</span>
                <span className="text-xs text-ink-500 ml-auto">Visual / diagram analysis</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-ink-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabledPhases.systemDesign}
                  onChange={(e) => setEnabledPhases(prev => ({ ...prev, systemDesign: e.target.checked }))}
                  className="rounded"
                  disabled={questionsGenerated}
                />
                <span><strong>Phase 3:</strong> System Design / Whiteboard</span>
                <span className="text-xs text-ink-500 ml-auto">Architecture drawing</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-ink-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabledPhases.codingChallenge}
                  onChange={(e) => setEnabledPhases(prev => ({ ...prev, codingChallenge: e.target.checked }))}
                  className="rounded"
                  disabled={questionsGenerated}
                />
                <span><strong>Phase 4:</strong> Coding Challenge</span>
                <span className="text-xs text-ink-500 ml-auto">Programming test</span>
              </label>
            </div>
          </div>

          {/* Q&A Configuration */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">
              <MessageSquare className="w-4 h-4 inline-block mr-1" />
              Q&A Session Configuration
            </label>
            <div className="bg-ink-800 border border-ink-700 rounded-lg p-4 space-y-4">
              {/* Total Questions Count */}
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  Total Number of Q&A Questions
                </label>
                <p className="text-xs text-ink-400 mb-2">
                  Set how many questions the candidate will be asked. AI will generate questions to fill any slots not covered by your custom questions.
                </p>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={totalQuestions}
                  onChange={(e) => {
                    const val = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                    setTotalQuestions(val);
                    // Trim custom questions if they exceed new total
                    if (customQuestions.length > val) {
                      setCustomQuestions(customQuestions.slice(0, val));
                    }
                  }}
                  disabled={questionsGenerated}
                  className="w-32 px-3 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                />
                {customQuestions.length > 0 && (
                  <span className="ml-3 text-xs text-ink-400">
                    ({customQuestions.length} custom + {Math.max(0, totalQuestions - customQuestions.length)} AI-generated)
                  </span>
                )}
              </div>

              {/* Custom Questions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-sm font-medium text-ink-300">
                      Custom Questions (Optional)
                    </label>
                    <p className="text-xs text-ink-400">
                      Add specific questions that must be asked. Include expected answer keywords to help evaluate responses.
                    </p>
                  </div>
                  <button
                    onClick={addCustomQuestion}
                    disabled={questionsGenerated || customQuestions.length >= totalQuestions}
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      questionsGenerated || customQuestions.length >= totalQuestions
                        ? 'bg-ink-800 text-ink-500 cursor-not-allowed'
                        : 'bg-brand-500/10 text-brand-400 hover:bg-brand-500/15 border border-brand-500/30'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    Add Question
                  </button>
                </div>

                {customQuestions.length === 0 && (
                  <p className="text-xs text-ink-500 italic py-2">
                    No custom questions added. All {totalQuestions} questions will be AI-generated based on the candidate's profile and job requirements.
                  </p>
                )}

                <div className="space-y-3">
                  {customQuestions.map((cq, index) => (
                    <div key={index} className="bg-ink-900 border border-ink-700 rounded-lg p-3 relative">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-500/15 text-brand-400 flex items-center justify-center text-xs font-bold mt-1">
                          {index + 1}
                        </span>
                        <div className="flex-1 space-y-2">
                          <div>
                            <input
                              type="text"
                              placeholder="Enter your question..."
                              value={cq.question}
                              onChange={(e) => updateCustomQuestion(index, 'question', e.target.value)}
                              disabled={questionsGenerated}
                              className="w-full px-3 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="Expected answer keywords (optional) e.g. REST, scalability, caching"
                              value={cq.expectedKeywords}
                              onChange={(e) => updateCustomQuestion(index, 'expectedKeywords', e.target.value)}
                              disabled={questionsGenerated}
                              className="w-full px-3 py-2 border border-ink-700 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent text-xs text-ink-300"
                            />
                          </div>
                        </div>
                        {!questionsGenerated && (
                          <button
                            onClick={() => removeCustomQuestion(index)}
                            className="flex-shrink-0 p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Remove question"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Difficulty Level */}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">
              Difficulty Level
            </label>
            <div className="bg-ink-800 border border-ink-700 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={difficultyLevel}
                  onChange={(e) => setDifficultyLevel(parseInt(e.target.value))}
                  disabled={questionsGenerated}
                  className="flex-1 h-2 bg-ink-700 rounded-lg appearance-none cursor-pointer accent-brand-500"
                />
                <span className={`px-3 py-1 rounded-full text-sm font-bold min-w-[90px] text-center ${
                  difficultyLevel <= 4 ? 'bg-green-500/15 text-green-300' :
                  difficultyLevel <= 6 ? 'bg-brand-500/15 text-brand-300' :
                  'bg-red-500/15 text-red-300'
                }`}>
                  {difficultyLevel}/10 {difficultyLevel <= 4 ? 'Easy' : difficultyLevel <= 6 ? 'Medium' : 'Hard'}
                </span>
              </div>
              <p className="text-xs text-ink-400 mt-2">
                Controls difficulty of generated questions and follow-up evaluation. 1-4 Easy, 5-6 Intermediate, 7-10 Hard.
              </p>
            </div>
          </div>

          {/* Questions Status */}
          {selectedCandidate && selectedRequirement && selectedOrganisation && (
            <div className="bg-brand-500/10 border border-brand-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {checkingQuestions ? (
                    <>
                      <Loader className="w-5 h-5 text-brand-400 animate-spin" />
                      <span className="text-sm text-brand-300">Checking questions status...</span>
                    </>
                  ) : questionsGenerated ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-green-300 font-medium">
                        ✅ Questions already generated - Ready to send!
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-orange-400" />
                      <span className="text-sm text-orange-300 font-medium">
                        ⚠️ Questions not generated yet
                      </span>
                    </>
                  )}
                </div>
              </div>

              {generateError && (
                <div className="mt-2 text-sm text-red-600">
                  Error: {generateError}
                </div>
              )}
            </div>
          )}

          {/* Questions Preview */}
          {questionsGenerated && (
            <div className="border border-ink-700 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setPreviewOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-ink-800 hover:bg-ink-700 transition-colors text-sm font-medium text-ink-200"
              >
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-brand-400" />
                  Preview Generated Questions
                  {previewQuestions.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-400 text-xs font-bold">
                      {previewQuestions.length}
                    </span>
                  )}
                </span>
                {previewOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {previewOpen && (
                <div className="bg-ink-900 p-4">
                  {loadingPreview ? (
                    <div className="flex items-center gap-2 text-sm text-ink-400 py-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Loading questions...
                    </div>
                  ) : previewQuestions.length === 0 ? (
                    <p className="text-sm text-ink-500 italic">No questions to preview.</p>
                  ) : (
                    <ol className="space-y-3">
                      {previewQuestions.map((q, i) => (
                        <li key={i} className="bg-ink-800 border border-ink-700 rounded-lg p-3">
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-500/15 text-brand-400 flex items-center justify-center text-xs font-bold mt-0.5">
                              {i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-ink-100 leading-relaxed">{q.text}</p>
                              {q.expected_keywords && (
                                <p className="mt-1.5 text-xs text-ink-400">
                                  <span className="text-ink-500 font-medium">Keywords: </span>
                                  {q.expected_keywords}
                                </p>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 flex-wrap">
            {/* Generate Questions Button */}
            <button
              onClick={handleGenerateQuestions}
              disabled={!selectedCandidate || !selectedRequirement || !selectedOrganisation || generating || questionsGenerated}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                !selectedCandidate || !selectedRequirement || !selectedOrganisation || questionsGenerated
                  ? 'bg-ink-800 text-ink-500 cursor-not-allowed'
                  : generating
                  ? 'bg-brand-400 text-ink-950 cursor-wait'
                  : 'bg-brand-500 text-ink-950 hover:bg-brand-400'
              }`}
            >
              {generating ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Generating Questions (30-60 sec)...
                </>
              ) : questionsGenerated ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Questions Generated
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Generate Questions
                </>
              )}
            </button>

            {/* Send Interview Button */}
            <button
              onClick={handleSendInterview}
              disabled={!selectedCandidate || !selectedRequirement || !selectedOrganisation || !questionsGenerated || sending}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                !questionsGenerated || sending
                  ? 'bg-ink-800 text-ink-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {sending ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Interview Invitation
                </>
              )}
            </button>

            {/* Get Test Link Button */}
            <button
              onClick={handleGetTestToken}
              disabled={!questionsGenerated || generatingToken}
              title="Generate a token without sending email — for testing only"
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium border transition-colors ${
                !questionsGenerated
                  ? 'border-ink-700 bg-ink-800 text-ink-500 cursor-not-allowed'
                  : generatingToken
                  ? 'border-amber-600 bg-amber-500/10 text-amber-400 cursor-wait'
                  : 'border-amber-600 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
              }`}
            >
              {generatingToken ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <FlaskConical className="w-4 h-4" />
              )}
              <span className="text-sm whitespace-nowrap">Get Test Link</span>
            </button>
          </div>

          {/* Test Token Panel */}
          {testToken && (
            <div className="border border-amber-600/40 bg-amber-500/5 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border-b border-amber-600/30">
                <FlaskConical className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-amber-400">Test Link — no email sent</span>
                {testToken.expires_at && (
                  <span className="ml-auto text-xs text-ink-400">
                    Expires: {new Date(testToken.expires_at).toLocaleString()}
                  </span>
                )}
              </div>

              <div className="p-4 space-y-3">
                {/* Token */}
                <div>
                  <p className="text-xs text-ink-500 font-medium mb-1.5 uppercase tracking-wide">Token</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-xs text-ink-200 font-mono break-all">
                      {testToken.token}
                    </code>
                    <button
                      onClick={() => copyToClipboard(testToken.token, 'token')}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-700 bg-ink-800 hover:bg-ink-700 text-xs font-medium transition-colors"
                    >
                      {copiedField === 'token' ? (
                        <><CheckCircle className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied</span></>
                      ) : (
                        <><Copy className="w-3.5 h-3.5 text-ink-300" /><span className="text-ink-300">Copy</span></>
                      )}
                    </button>
                  </div>
                </div>

                {/* Full URL */}
                <div>
                  <p className="text-xs text-ink-500 font-medium mb-1.5 uppercase tracking-wide">Interview URL</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg">
                      <Link className="w-3.5 h-3.5 text-ink-500 flex-shrink-0" />
                      <span className="text-xs text-brand-400 font-mono break-all">{testToken.interview_url}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(testToken.interview_url, 'url')}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-700 bg-ink-800 hover:bg-ink-700 text-xs font-medium transition-colors"
                    >
                      {copiedField === 'url' ? (
                        <><CheckCircle className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied</span></>
                      ) : (
                        <><Copy className="w-3.5 h-3.5 text-ink-300" /><span className="text-ink-300">Copy</span></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sent Invitation Token Panel */}
          {sentInvitationResult && (
            <div className="border border-green-600/40 bg-green-500/5 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-green-500/10 border-b border-green-600/30">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-green-400">Invitation Sent — Token ID for manual sharing</span>
                {sentInvitationResult.email && (
                  <span className="ml-auto text-xs text-ink-400">Email sent to: {sentInvitationResult.email}</span>
                )}
                <button
                  onClick={() => setSentInvitationResult(null)}
                  className="ml-2 text-ink-500 hover:text-ink-300 text-xs"
                  title="Dismiss"
                >✕</button>
              </div>

              <div className="p-4 space-y-3">
                {/* Token */}
                <div>
                  <p className="text-xs text-ink-500 font-medium mb-1.5 uppercase tracking-wide">Token ID</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-xs text-ink-200 font-mono break-all">
                      {sentInvitationResult.token}
                    </code>
                    <button
                      onClick={() => copyToClipboard(sentInvitationResult.token, 'sent-token')}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-700 bg-ink-800 hover:bg-ink-700 text-xs font-medium transition-colors"
                    >
                      {copiedField === 'sent-token' ? (
                        <><CheckCircle className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied</span></>
                      ) : (
                        <><Copy className="w-3.5 h-3.5 text-ink-300" /><span className="text-ink-300">Copy</span></>
                      )}
                    </button>
                  </div>
                </div>

                {/* Full URL */}
                {sentInvitationResult.interview_url && (
                  <div>
                    <p className="text-xs text-ink-500 font-medium mb-1.5 uppercase tracking-wide">Interview URL</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg">
                        <Link className="w-3.5 h-3.5 text-ink-500 flex-shrink-0" />
                        <span className="text-xs text-brand-400 font-mono break-all">{sentInvitationResult.interview_url}</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(sentInvitationResult.interview_url, 'sent-url')}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-700 bg-ink-800 hover:bg-ink-700 text-xs font-medium transition-colors"
                      >
                        {copiedField === 'sent-url' ? (
                          <><CheckCircle className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied</span></>
                        ) : (
                          <><Copy className="w-3.5 h-3.5 text-ink-300" /><span className="text-ink-300">Copy</span></>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="bg-ink-800 rounded-lg p-4 text-sm text-ink-300">
            <p className="font-medium mb-2">How it works:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Select candidate, job requirement, and organization</li>
              <li>Set total Q&A question count and add any custom questions (optional)</li>
              <li>Click "Generate Questions" (takes 30-60 seconds) — AI fills remaining slots</li>
              <li>Once generated, click "Send Interview Invitation"</li>
              <li>Candidate will receive an email with a secure interview link</li>
              <li>Link expires in 48 hours and can only be used once</li>
            </ol>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-semibold text-ink-100">Interview History</h2>
              <p className="text-sm text-ink-300 mt-1">
                View all sent interview invitations and their status
              </p>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-300 bg-ink-900 border border-ink-700 rounded-lg hover:bg-ink-800 transition-colors"
            >
              <Loader className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* History Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-800">
              <thead className="bg-ink-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Sent At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Expires At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Proctor / Panel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ink-900 divide-y divide-ink-800">
                {sentInterviews.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-ink-400">
                      No interviews sent yet
                    </td>
                  </tr>
                ) : (
                  sentInterviews.map((interview) => {
                    const orgProctors = proctors.filter(
                      (p) => p.organization_id === interview.organisation_id
                    );
                    return (
                    <tr key={interview.token} className="hover:bg-ink-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink-100">
                        {interview.candidate_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-400">
                        {interview.email_sent_to}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-400">
                        {new Date(interview.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-400">
                        {new Date(interview.expires_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {interview.interview_type === 'panel' ? (
                          <div className="flex items-center gap-1.5 text-xs text-ink-200">
                            <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                            <span title={(interview.panel_interviewer_names || []).join(', ')}>
                              Panel: {(interview.panel_interviewer_names || []).join(', ') || '—'}
                            </span>
                          </div>
                        ) : (
                          <select
                            value={interview.assigned_proctor_id || ''}
                            onChange={(e) => handleReassignProctor(interview.token, e.target.value)}
                            className="px-2 py-1 bg-ink-900 border border-ink-700 rounded-md text-xs text-ink-200 focus:ring-1 focus:ring-brand-500"
                            title={interview.assigned_proctor_name || 'Unassigned'}
                          >
                            <option value="">Unassigned</option>
                            {interview.assigned_proctor_id &&
                              !orgProctors.some((p) => p.user_id === interview.assigned_proctor_id) && (
                                <option value={interview.assigned_proctor_id}>
                                  {interview.assigned_proctor_name || 'Current'}
                                </option>
                              )}
                            {orgProctors.map((p) => (
                              <option key={p.user_id} value={p.user_id}>{p.full_name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(interview)}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SendInterview;